import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useGetPublicationRanking } from '../services/appService';

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const getDefaultRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    from: toDateInputValue(firstDay),
    to: toDateInputValue(lastDay),
  };
};

const getPreviousMonthRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);

  return {
    from: toDateInputValue(firstDay),
    to: toDateInputValue(lastDay),
  };
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('de-DE', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const escapeCsv = (value) => {
  const stringValue = value == null ? '' : String(value);
  if (/[";\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

function PublicationRankingPage() {
  const { appId } = useParams();
  const getPublicationRanking = useGetPublicationRanking();
  const defaultRange = useMemo(() => getDefaultRange(), []);
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [includeArticles, setIncludeArticles] = useState(true);
  const [includeEvents, setIncludeEvents] = useState(false);
  const [ranking, setRanking] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const selectedTypes = useMemo(() => {
    const types = [];
    if (includeArticles) types.push('articles');
    if (includeEvents) types.push('events');
    return types;
  }, [includeArticles, includeEvents]);

  const fetchRanking = useCallback(async () => {
    if (!appId) return;
    if (selectedTypes.length === 0) {
      setError('Bitte mindestens Artikel oder Veranstaltungen auswählen.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await getPublicationRanking(appId, {
        from,
        to,
        types: selectedTypes.join(','),
      });
      setRanking(data);
      setExpandedRows({});
    } catch (err) {
      console.error('Failed to load publication ranking:', err);
      setError(err.message || 'Auswertung konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [appId, from, getPublicationRanking, selectedTypes, to]);

  useEffect(() => {
    fetchRanking();
  }, [fetchRanking]);

  const setCurrentMonth = () => {
    const range = getDefaultRange();
    setFrom(range.from);
    setTo(range.to);
  };

  const setPreviousMonth = () => {
    const range = getPreviousMonthRange();
    setFrom(range.from);
    setTo(range.to);
  };

  const setApril2026 = () => {
    setFrom('2026-04-01');
    setTo('2026-04-30');
  };

  const toggleRow = (publisherKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [publisherKey]: !prev[publisherKey],
    }));
  };

  const handleExportCsv = () => {
    if (!ranking?.rows?.length) return;

    const rows = [
      ['Rang', 'Name', 'E-Mail', 'Benutzername', 'Typ', 'Anzahl', 'Letzte Veröffentlichung', 'Veröffentlichungen'],
      ...ranking.rows.map((row) => [
        row.rank,
        row.publisher_name,
        row.publisher_email || '',
        row.publisher_username || '',
        row.publisher_type,
        row.publication_count,
        formatDateTime(row.last_publication_at),
        (row.publications || [])
          .map((item) => `${item.title} (${item.type}, ${formatDate(item.published_at || item.created_at)})`)
          .join(' | '),
      ]),
    ];

    const csv = rows.map((row) => row.map(escapeCsv).join(';')).join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `publication-ranking-${from}_${to}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Veröffentlichungs-Ranking
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Auswertung, wer im gewählten Zeitraum Inhalte in der DorfApp veröffentlicht hat.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<PrintIcon />} variant="outlined" onClick={() => window.print()} disabled={!ranking?.rows?.length}>
            Drucken
          </Button>
          <Button startIcon={<DownloadIcon />} variant="outlined" onClick={handleExportCsv} disabled={!ranking?.rows?.length}>
            CSV
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }} className="no-print">
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Von"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Bis"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={<Checkbox checked={includeArticles} onChange={(event) => setIncludeArticles(event.target.checked)} />}
                label="Artikel"
              />
              <FormControlLabel
                control={<Checkbox checked={includeEvents} onChange={(event) => setIncludeEvents(event.target.checked)} />}
                label="Veranstaltungen"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="text" onClick={setApril2026}>April 2026</Button>
                <Button variant="text" onClick={setCurrentMonth}>Dieser Monat</Button>
                <Button variant="text" onClick={setPreviousMonth}>Letzter Monat</Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button startIcon={<RefreshIcon />} variant="contained" onClick={fetchRanking} disabled={loading}>
                Auswertung laden
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>Auswertung wird geladen…</Typography>
        </Box>
      )}

      {!loading && ranking && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">
              Zeitraum: {formatDate(ranking.from)} – {formatDate(ranking.to)}
            </Typography>
            <Typography color="text.secondary">
              {ranking.total_publishers} Veröffentlichende · {ranking.total_publications} Veröffentlichungen · Zeitzone {ranking.timezone}
            </Typography>
          </Box>

          {ranking.rows?.length ? (
            <TableContainer component={Paper}>
              <Table aria-label="publication ranking table">
                <TableHead>
                  <TableRow>
                    <TableCell width={72}>Rang</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>E-Mail / Username</TableCell>
                    <TableCell>Typ</TableCell>
                    <TableCell align="right">Anzahl</TableCell>
                    <TableCell>Letzte Veröffentlichung</TableCell>
                    <TableCell width={80} align="center">Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ranking.rows.map((row) => (
                    <React.Fragment key={row.publisher_key}>
                      <TableRow hover>
                        <TableCell>{row.rank}</TableCell>
                        <TableCell>
                          <Typography fontWeight={600}>{row.publisher_name}</Typography>
                        </TableCell>
                        <TableCell>
                          {row.publisher_email || row.publisher_username ? (
                            <>
                              {row.publisher_email && <Typography variant="body2">{row.publisher_email}</Typography>}
                              {row.publisher_username && <Typography variant="caption" color="text.secondary">@{row.publisher_username}</Typography>}
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>{row.publisher_type === 'organization' ? 'Organisation' : row.publisher_type === 'user' ? 'User' : 'Redaktion'}</TableCell>
                        <TableCell align="right">{row.publication_count}</TableCell>
                        <TableCell>{formatDateTime(row.last_publication_at)}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => toggleRow(row.publisher_key)} aria-label="Details anzeigen">
                            {expandedRows[row.publisher_key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, borderBottom: expandedRows[row.publisher_key] ? undefined : 0 }}>
                          <Collapse in={!!expandedRows[row.publisher_key]} timeout="auto" unmountOnExit>
                            <Box sx={{ m: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Veröffentlichungen
                              </Typography>
                              {(row.publications || []).map((item) => (
                                <Box key={`${item.type}-${item.id}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
                                  <Typography>{item.title}</Typography>
                                  <Typography color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    {item.type === 'article' ? 'Artikel' : 'Veranstaltung'} · {formatDate(item.published_at || item.created_at)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">Für diesen Zeitraum wurden keine Veröffentlichungen gefunden.</Alert>
          )}
        </>
      )}
    </Box>
  );
}

export default PublicationRankingPage;
